/**
 * Market Pill Component
 *
 * Minimal collapsed state of the widget (APPS-4228 — dark mode). A CTA at the
 * configured position: Predictor brand mark + the localized "Place a
 * prediction through Predictor.io" text, the same at every position
 * (APPS-5583). Clicking expands to the preview.
 *
 * Intentionally dumb — shows iff `visible` is true. The entrance animation
 * (slideUp when floating, fade when inline) fires naturally on mount. No exit
 * animation because the sibling surface (preview) slides in on top when the
 * pill is replaced, hiding the change.
 *
 * The `AdvertisementLabel` header sits outside the button, so the surface is a
 * wrapper (position / width / animation) around the label plus the button
 * (chrome only). The wrapper stays `display: block` rather than a flex column:
 * the inline position class is `pw:block`, and two competing `display`
 * utilities would resolve by stylesheet order rather than intent.
 */

import type { JSX } from "preact";
import { getConfig } from "../config";
import { messages } from "../i18n";
import {
  floatingPositionClassByPosition,
  pillSurfaceShellClasses,
  surfaceAnimationClass,
  surfaceClickTargetClass,
  WidgetPosition,
  widgetSurfaceWidthClass,
} from "../utils/constants";
import { AdvertisementLabel } from "./advertisement-label";
import { PredictorBrandIcon } from "./predictor-brand-icon";

export interface MarketPillProps {
  visible: boolean;
  onClick: () => void;
}

// Shared shell chrome (`pillSurfaceShellClasses`) plus the interactive-only
// bits. `w-full` makes the button span the wrapper: the fixed floating width,
// or the article column inline. That also keeps the label's hairlines flush
// with the pill's edges.
const staticPillClasses = [
  pillSurfaceShellClasses,
  surfaceClickTargetClass,
  "pw:w-full pw:cursor-pointer",
].join(" ");

export function MarketPill({
  visible,
  onClick,
}: MarketPillProps): JSX.Element | null {
  if (!visible) return null;

  const m = messages();
  const position = getConfig().position;
  const positionClass = floatingPositionClassByPosition[position];
  const widthClass = widgetSurfaceWidthClass(position, "pill");
  const animationClass = surfaceAnimationClass(position, false);
  const isInline = position === WidgetPosition.Inline;

  return (
    <div
      className={`${positionClass} ${widthClass} ${animationClass} pw:font-sans`}
    >
      <AdvertisementLabel />
      <button
        aria-label={m.pill.ariaLabel}
        className={staticPillClasses}
        onClick={onClick}
        type="button"
      >
        {/* 55px inline, where the pill spans the article column and reads as
            embedded content; 48px floating, which fills the 56px the two-line
            copy makes the pill (2 × 24px leading + 8px `py-1`). */}
        <PredictorBrandIcon
          className={
            isInline
              ? "pw:h-[3.4375rem] pw:w-[3.4375rem] pw:flex-shrink-0"
              : "pw:h-12 pw:w-12 pw:flex-shrink-0"
          }
        />
        {/* The copy is longer than the floating pill is wide, so there it
            always folds to two lines, as Figma draws it. Inline it folds only
            in a narrow article column. Either way it wraps rather than
            overflowing. */}
        {/* `text-left` overrides the button element's UA-default centring,
            which only becomes visible once the copy wraps. */}
        <span className="pw:text-[1.0625rem] pw:font-bold pw:leading-6 pw:min-w-0 pw:text-left">
          {m.pill.cta}
        </span>
      </button>
    </div>
  );
}
